if (!customElements.get('localization-form')) {
  customElements.define(
      'localization-form',
      class LocalizationForm extends HTMLElement {
        constructor() {
          super();
          this.mql = window.matchMedia('(min-width: 750px)');
          this.header = this.closest('.header-wrapper') || document.querySelector('.header-wrapper');
          this.drawerContainer = this.closest('.menu-drawer, .header-navigation__mobile-drawer');
          this.pendingItemClick = false;
          this.clearPendingItemClick = null;
          this.countryFilterApplied = false;
          this.elements = {
            input: this.querySelector(
              'input[name="language_code"], input[name="locale_code"], input[name="country_code"]'
            ),
            button: this.querySelector('button.localization-form__select'),
            panel: this.querySelector('.disclosure__list-wrapper'),
            search: this.querySelector('input[name="country_filter"]'),
            closeButton: this.querySelector('.country-selector__close-button'),
            resetButton: this.querySelector('.country-filter__reset-button'),
            searchIcon: this.querySelector('.country-filter__search-icon'),
            liveRegion: this.querySelector('[data-country-search-results]'),
          };
          this.addEventListener('keyup', this.onContainerKeyUp.bind(this));
          this.addEventListener('keydown', this.onContainerKeyDown.bind(this));
          this.addEventListener('focusout', this.closeSelector.bind(this));
          this.elements.button.addEventListener('click', this.openSelector.bind(this));

          if (this.elements.search) {
            this.elements.search.addEventListener('keyup', this.filterCountries.bind(this));
            this.elements.search.addEventListener('focus', this.onSearchFocus.bind(this));
            this.elements.search.addEventListener('blur', this.onSearchBlur.bind(this));
            this.elements.search.addEventListener('keydown', this.onSearchKeyDown.bind(this));
          }
          if (this.elements.closeButton) {
            this.elements.closeButton.addEventListener('click', this.hidePanel.bind(this));
          }
          if (this.elements.resetButton) {
            this.elements.resetButton.addEventListener('click', this.resetFilter.bind(this));
            this.elements.resetButton.addEventListener('mousedown', (event) => event.preventDefault());
          }

          this.querySelectorAll('a').forEach((item) => {
            item.addEventListener('mousedown', () => this.markPendingItemClick());
            item.addEventListener('click', this.onItemClick.bind(this));
          });

          // Header-navigation country lists arrive empty and are built from JSON (renderCountryList),
          // so their rows use delegated listeners with the same behaviour as the per-row ones above.
          // They are built on first use only: the press on the selector button (ahead of its click),
          // or openSelector for a keyboard open. Building both 88-row lists at idle cost that work
          // (about 1,300 elements) on every page view, whether the selector was opened or not.
          if (this.querySelector('ul[data-hb-country-list]')) {
            const lazyRowSelector = 'ul[data-hb-country-list] a';
            this.addEventListener('mousedown', (event) => {
              if (event.target.closest?.(lazyRowSelector)) this.markPendingItemClick();
            });
            this.addEventListener('click', (event) => {
              const item = event.target.closest?.(lazyRowSelector);
              if (item && this.contains(item)) this.onItemClick(event, item);
            });
            this.elements.button.addEventListener('pointerdown', () => this.renderCountryList(), {
              once: true,
              passive: true,
            });
          }
        }

        renderCountryList() {
          const list = this.querySelector('ul[data-hb-country-list]:not([data-rendered])');
          if (!list) return;
          list.setAttribute('data-rendered', '');

          let data = null;
          let parseError = null;
          try {
            data = JSON.parse(document.getElementById(list.dataset.source)?.textContent || 'null');
          } catch (error) {
            data = null;
            parseError = error;
          }
          if (!data || !Array.isArray(data.countries)) {
            // data-rendered stays set so this never retries, and the selector keeps an empty list;
            // nothing else on the page would say why.
            const message = `[Localization] Country list JSON #${list.dataset.source} is missing or invalid.`;
            if (parseError) console.warn(message, parseError);
            else console.warn(message);
            return;
          }

          const escapeHtml = (value) =>
            String(value ?? '').replace(
              /[&<>"']/g,
              (char) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[char]
            );
          const current = list.dataset.currentCountry || '';
          const prefix = escapeHtml(list.dataset.listPrefix || '');
          const withFlags = list.hasAttribute('data-flags');
          const currencyClass =
            'localization-form__currency motion-reduce' + (list.dataset.showCurrencies === 'true' ? '' : ' hidden');
          const check = String(data.check ?? '');
          // Shopify lists countries in English-name order; sort by the shown (translated) name instead.
          const collator = new Intl.Collator(document.documentElement.lang || undefined, { sensitivity: 'base' });
          data.countries.sort((a, b) => collator.compare(String(a[1] ?? ''), String(b[1] ?? '')));

          // Same markup, text and whitespace as the Liquid row loop in snippets/country-localization.liquid,
          // so the rows render identically and the search filter reads the same .country text.
          list.innerHTML = data.countries
            .map(([code, name, currency, symbol, language]) => {
              const isCurrent = code === current;
              const lowerCode = escapeHtml(String(code).toLowerCase());
              const flag =
                withFlags && String(code).length === 2
                  ? '\n                  <img\n                    src="https://hatscripts.github.io/circle-flags/flags/' +
                    lowerCode +
                    '.svg"\n                    alt=""\n                    class="header-navigation__mobile-list-country-flag"\n                    width="24"\n                    height="24"\n                    loading="lazy"\n                  >\n                '
                  : '';
              return (
                '<li class="disclosure__item" tabindex="-1">\n              ' +
                '<a class="link link--text disclosure__link caption-large focus-inset" href="#"' +
                (isCurrent ? ' aria-current="true"' : '') +
                ` data-value="${escapeHtml(code)}"` +
                (language !== null && language !== undefined ? ` data-default-language="${escapeHtml(language)}"` : '') +
                ` id="${prefix}-${lowerCode}">` +
                `\n                <span${isCurrent ? '' : ' class="visibility-hidden"'}>${check}</span>` +
                `\n                ${flag}\n                ` +
                `<span class="country">${escapeHtml(name)}<span class="${currencyClass}">` +
                `\n                    (${escapeHtml(currency)} ${escapeHtml(symbol)})\n                  </span>` +
                '\n                </span>\n              </a>\n            </li>'
              );
            })
            .join('');
        }

        getWelcomePopupLocale(countryCode) {
          try {
            const match = document.cookie.match(/(?:^|;\s*)hb_welcome_country_popup=([^;]*)/);
            if (!match) return '';
            const cookieValue = decodeURIComponent(match[1]).trim();
            if (!cookieValue || cookieValue === '1') return '';

            const savedSelection = JSON.parse(cookieValue);
            if (!savedSelection || typeof savedSelection !== 'object') return '';

            const savedCountry = String(savedSelection.country || '').toUpperCase();
            const targetCountry = String(countryCode || '').toUpperCase();
            const savedLocale = String(savedSelection.locale || '').trim();

            return savedCountry && targetCountry && savedCountry === targetCountry && savedLocale ? savedLocale : '';
          } catch (_) {
            return '';
          }
        }

        applyWelcomePopupLocale(form, countryCode) {
          const locale = this.getWelcomePopupLocale(countryCode);
          if (!locale || !form) return;

          let localeInput =
            form.querySelector('input[name="language_code"]') ||
            form.querySelector('input[name="locale_code"]');
          if (!localeInput) {
            localeInput = document.createElement('input');
            localeInput.type = 'hidden';
            localeInput.name = 'language_code';
            form.appendChild(localeInput);
          }

          localeInput.disabled = false;
          localeInput.value = locale;
        }

        applyCountryDefaultLanguage(form, languageCode) {
          if (!form || !languageCode) return;

          const currentLanguageInput = form.querySelector('input[name="current_language_code"]');
          if (currentLanguageInput) currentLanguageInput.disabled = true;

          let localeInput =
            form.querySelector('input[name="language_code"]') ||
            form.querySelector('input[name="locale_code"]');
          if (!localeInput) {
            localeInput = document.createElement('input');
            localeInput.type = 'hidden';
            localeInput.name = 'language_code';
            form.appendChild(localeInput);
          }

          localeInput.disabled = false;
          localeInput.name = 'language_code';
          localeInput.value = languageCode;
        }

        hidePanel() {
          this.clearPendingItemClick?.();
          this.elements.button.setAttribute('aria-expanded', 'false');
          this.elements.panel.setAttribute('hidden', true);
          if (this.elements.search) {
            // Only re-filter when there is a filter to clear (and the rows exist). Closing the drawer
            // calls this for every form, and an unbuilt lazy list would report '0 results'.
            const hasFilterToClear = Boolean(this.elements.search.value) || this.countryFilterApplied;
            this.elements.search.value = '';
            if (hasFilterToClear && !this.querySelector('ul[data-hb-country-list]:not([data-rendered])')) {
              this.filterCountries();
            }
          }
          document.body.classList.remove('overflow-hidden-mobile');
          this.drawerContainer?.classList.remove('country-selector-open');
          if (this.header) {
            this.header.preventHide = false;
          }
        }

        onContainerKeyDown(event) {
          const focusableItems = Array.from(this.querySelectorAll('a')).filter(
              (item) => !item.parentElement.classList.contains('hidden')
          );
          let focusedItemIndex = focusableItems.findIndex((item) => item === document.activeElement);
          let itemToFocus;

          switch ((event.code || event.key || '').toUpperCase()) {
            case 'ARROWUP':
              event.preventDefault();
              itemToFocus =
                  focusedItemIndex > 0 ? focusableItems[focusedItemIndex - 1] : focusableItems[focusableItems.length - 1];
              itemToFocus?.focus();
              break;
            case 'ARROWDOWN':
              event.preventDefault();
              itemToFocus =
                  focusedItemIndex < focusableItems.length - 1 ? focusableItems[focusedItemIndex + 1] : focusableItems[0];
              itemToFocus?.focus();
              break;
          }


        }

        onContainerKeyUp(event) {
          event.preventDefault();

          switch ((event.code || (event.key === ' ' ? 'Space' : event.key) || '').toUpperCase()) {
            case 'ESCAPE':
              if (this.elements.button.getAttribute('aria-expanded') == 'false') return;
              this.hidePanel();
              event.stopPropagation();
              this.elements.button.focus();
              break;
            case 'SPACE':
              if (this.elements.button.getAttribute('aria-expanded') == 'true') return;
              this.openSelector();
              break;
          }
        }

        onItemClick(event, item = event.currentTarget) {
          event.preventDefault();
          this.pendingItemClick = false;
          const form = this.querySelector('form');
          const selectedValue = item.dataset.value;
          if (!selectedValue || !form) return;

          const isLanguagePanel = Boolean(this.elements.panel?.classList.contains('language-selector'));
          const localeInput =
            form.querySelector('input[name="language_code"]') ||
            form.querySelector('input[name="locale_code"]');
          const countryInput = form.querySelector('input[name="country_code"]');

          if (isLanguagePanel) {
            if (localeInput) {
              localeInput.disabled = false;
              localeInput.name = 'language_code';
              localeInput.value = selectedValue;
            }
            // Keep current country so Markets resolve FR+en correctly (same as welcome popup).
            if (countryInput && !countryInput.value) {
              countryInput.value = window.Shopify?.country || '';
            }
          } else if (countryInput) {
            countryInput.value = selectedValue;
            this.applyCountryDefaultLanguage(
              form,
              item.dataset.defaultLanguage || this.getWelcomePopupLocale(selectedValue)
            );
          } else if (this.elements.input) {
            this.elements.input.value = selectedValue;
          }

          window.hbCountryRouting?.confirm(countryInput?.value, localeInput?.value);
          form.submit();
        }

        openSelector() {
          this.clearPendingItemClick?.();
          this.renderCountryList();
          document.querySelectorAll('details-modal.header__search').forEach((search) => {
            if (search.isOpen()) search.close(false);
          });
          this.elements.button.focus();
          this.elements.panel.toggleAttribute('hidden');
          this.elements.button.setAttribute(
              'aria-expanded',
              (this.elements.button.getAttribute('aria-expanded') === 'false').toString()
          );
          if (!document.body.classList.contains('overflow-hidden-tablet')) {
            document.body.classList.add('overflow-hidden-mobile');
          }
          if (this.elements.search && this.mql.matches) {
            this.elements.search.focus();
          }
          if (this.hasAttribute('data-prevent-hide') && this.header) {
            this.header.preventHide = true;
          }
          this.drawerContainer?.classList.add('country-selector-open');
        }

        // Set on mousedown of a row so the focusout that can come before its click (Safari does not focus
        // a clicked link, so focus drops to the body) does not close the panel under the pointer. It is
        // cleared at the window in the capture phase, because the click may never reach onItemClick:
        // country-change-modal.js stops clicks on other countries at the document. A flag left set would
        // stop every later outside click, overlay tap and Tab out closing the panel, so a press that never
        // becomes a click (right or ctrl click, released off the row, a native link drag, or a press
        // whose mouseup is lost to an app switch) clears it too.
        markPendingItemClick() {
          this.pendingItemClick = true;
          if (this.clearPendingItemClick) return;
          const clearOn = ['click', 'mousedown', 'contextmenu', 'dragend'];
          let releaseTimer = 0;
          // The click, when there is one, is dispatched right after mouseup, so let it run first.
          const clearAfterRelease = () => {
            releaseTimer = window.setTimeout(clear);
          };
          // In the capture phase the window also sees every element blur, including the one this
          // flag exists to survive (focus leaving the search field as the row is pressed), so only
          // the window losing focus counts.
          const clearOnWindowBlur = (event) => {
            if (event.target === window) clear();
          };
          const clear = () => {
            window.clearTimeout(releaseTimer);
            this.pendingItemClick = false;
            this.clearPendingItemClick = null;
            clearOn.forEach((type) => window.removeEventListener(type, clear, true));
            window.removeEventListener('mouseup', clearAfterRelease, true);
            window.removeEventListener('blur', clearOnWindowBlur, true);
          };
          this.clearPendingItemClick = clear;
          clearOn.forEach((type) => window.addEventListener(type, clear, true));
          window.addEventListener('mouseup', clearAfterRelease, true);
          window.addEventListener('blur', clearOnWindowBlur, true);
        }

        closeSelector(event) {
          if (this.pendingItemClick) return;
          // Keep the panel open under the Change country modal, so "Stay on site" can hand focus back to
          // the row that opened it.
          if (event.relatedTarget?.closest?.('#CountryChangeModal')) return;

          if (
              event.target.classList.contains('country-selector__overlay') ||
              !this.contains(event.target) ||
              !this.contains(event.relatedTarget)
          ) {
            this.hidePanel();
          }
        }

        normalizeString(str) {
          return str
              .normalize('NFD')
              .replace(/\p{Diacritic}/gu, '')
              .toLowerCase();
        }

        // Extra search keys beyond country name (keep name matching as primary).
        getCountrySearchAliases() {
          return {
            uae: 'AE',
            'u.a.e': 'AE',
            'u.a.e.': 'AE',
            uk: 'GB',
            usa: 'US',
            ksa: 'SA',
          };
        }

        countryMatchesSearch(countryName, countryCode, searchValue) {
          if (!searchValue) return true;

          const normalizedName = this.normalizeString(countryName || '');
          if (normalizedName.indexOf(searchValue) > -1) return true;

          const iso = String(countryCode || '').toUpperCase();
          if (this.normalizeString(iso) === searchValue) return true;

          const aliasIso = this.getCountrySearchAliases()[searchValue];
          return Boolean(aliasIso && aliasIso === iso);
        }

        filterCountries() {
          const searchValue = this.normalizeString(this.elements.search.value);
          const popularCountries = this.querySelector('.popular-countries');
          const allCountries = this.querySelectorAll('a');
          let visibleCountries = 0;
          this.countryFilterApplied = Boolean(searchValue);

          this.elements.resetButton.classList.toggle('hidden', !searchValue);
          this.elements.searchIcon.classList.toggle('country-filter__search-icon--hidden', Boolean(searchValue));

          if (popularCountries) {
            popularCountries.classList.toggle('hidden', searchValue);
          }

          allCountries.forEach((item) => {
            const countryName = item.querySelector('.country')?.textContent || '';
            const matches = this.countryMatchesSearch(countryName, item.dataset.value, searchValue);
            item.parentElement.classList.toggle('hidden', !matches);
            if (matches) visibleCountries += 1;
          });

          if (this.elements.liveRegion) {
            this.elements.liveRegion.innerHTML = window.accessibilityStrings.countrySelectorSearchCount.replace(
                '[count]',
                visibleCountries
            );
          }

          this.querySelector('.country-selector').scrollTop = 0;
          this.querySelector('.country-selector__list').scrollTop = 0;
        }

        resetFilter(event) {
          event.stopPropagation();
          this.elements.search.value = '';
          this.filterCountries();
          this.elements.search.focus();
        }

        onSearchFocus() {
          if (this.elements.search.value) {
            this.elements.searchIcon.classList.add('country-filter__search-icon--hidden');
          }
        }

        onSearchBlur() {
          this.elements.searchIcon.classList.toggle(
              'country-filter__search-icon--hidden',
              Boolean(this.elements.search.value)
          );
        }

        onSearchKeyDown(event) {
          if ((event.code || event.key || '').toUpperCase() === 'ENTER') {
            event.preventDefault();
          }
        }
      }
  );
}
